import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ICompany } from '@nxsys/types/models/company';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppButton from '@nxsys/components/AppButton';
import AppAlert from '@nxsys/components/AppAlert';
import AppSelect from '@nxsys/components/AppSelect';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { AutoComplete, Form, Row, Col, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import {
  useGetZohoSettings,
  useConnectZoho,
  useUpdateZohoSettings,
  useGetZohoTemplates,
  useGetZohoTemplateDetails,
} from '@nxsys/hooks/api/company/useCompanyApi';
import { showMessage } from '@nxsys/utils/message';
import { Icons } from '@nxsys/types/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useSelector } from 'react-redux';
import { IRELAND } from '@nxsys/store/constants/keys';
import { socketZohoSignContract } from '@nxsys/services/sockets';
import { AppState } from '@nxsys/store';

export type ZohoSettingsFormRef = {
  submit: () => void;
};

export type ZohoSettingsFormValues = {
  clientId: string;
  clientSecret: string;
};

type ZohoSettingsFormProps = {
  companyData?: ICompany;
};

enum ZohoTab {
  CONNECT = 'connectZoho',
  CIS = 'cisTemplate',
  NON_CIS = 'nonCisTemplate',
  PAYE = 'payeTemplate',
  UMBRELLA = 'umbrellaTemplate',
  HYBRID = 'hybridTemplate',
  PAYE_HYBRID = 'payeHybridTemplate',
}

const TEMPLATE_TABS = [
  { tab: 'CIS TEMPLATE', key: ZohoTab.CIS, mappingKey: 'cis' },
  { tab: 'NON_CIS TEMPLATE', key: ZohoTab.NON_CIS, mappingKey: 'nonCis' },
  { tab: 'PAYE TEMPLATE', key: ZohoTab.PAYE, mappingKey: 'paye' },
  { tab: 'UMBRELLA TEMPLATE', key: ZohoTab.UMBRELLA, mappingKey: 'umbrella' },
  { tab: 'UMBRELLA HYBRID TEMPLATE', key: ZohoTab.HYBRID, mappingKey: 'hybrid' },
  { tab: 'PAYE HYBRID TEMPLATE', key: ZohoTab.PAYE_HYBRID, mappingKey: 'payeHybrid' },
];

// Nxsys onboarding fields that can be mapped to Zoho template fields
const ONBOARDING_FIELDS = [
  'forename',
  'surname',
  'email',
  'mobile',
  'address1',
  'address2',
  'city',
  'county',
  'postCode',
  'country',
  'DOB',
  'NINumber',
  'nationality',
  'bankName',
  'accountName',
  'accountNumber',
  'sortCode',
];

// Template mapping component (shared by all 6 template tabs)
const TemplateMappingTab: React.FC<{
  companyId: string;
  mappingKey: string;
  zohoSettings: any;
  onSave: (templateId: string, mapping: Record<string, string>) => void;
  saving?: boolean;
}> = ({ companyId, mappingKey, zohoSettings, onSave, saving }) => {
  const { value: templates, run: fetchTemplates, loading: templatesLoading } = useGetZohoTemplates();
  const { value: templateDetails, run: fetchTemplateDetails, loading: detailsLoading } = useGetZohoTemplateDetails();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);

  const templateIdKey = `${mappingKey}TemplateId`;
  const mappingDataKey = `${mappingKey}Mapping`;

  useEffect(() => {
    fetchTemplates(companyId);
  }, [companyId]);

  // Restore saved template + mapping from zohoSettings (once)
  useEffect(() => {
    if (zohoSettings && !initialized) {
      const savedTemplateId = zohoSettings[templateIdKey];
      const savedMapping = zohoSettings[mappingDataKey];
      if (savedTemplateId) {
        setSelectedTemplateId(savedTemplateId);
        fetchTemplateDetails(companyId, savedTemplateId);
      }
      if (savedMapping && typeof savedMapping === 'object') {
        setMapping(savedMapping);
      }
      setInitialized(true);
    }
  }, [zohoSettings, templateIdKey, mappingDataKey, initialized]);

  const templateOptions = useMemo(() => {
    return (templates?.templates ?? []).map((t: any) => ({
      label: t.template_name ?? t.name ?? t.id,
      value: t.template_id ?? t.id,
    }));
  }, [templates]);

  // Extract fields from template details
  const templateFields = useMemo(() => {
    if (!templateDetails?.templates) return [];
    const fields = templateDetails.templates?.actions?.[0]?.fields ?? [];
    return fields.map((f: any) => ({
      label: f.field_name ?? f.display_name ?? f,
      value: f.field_name ?? f,
    }));
  }, [templateDetails]);

  const handleSelectTemplate = (option: any) => {
    const templateId = option?.value;
    setSelectedTemplateId(templateId);
    setMapping({});
    if (templateId) {
      fetchTemplateDetails(companyId, templateId);
    }
  };

  const handleMappingChange = (fieldName: string, zohoField: string) => {
    setMapping((prev) => ({ ...prev, [fieldName]: zohoField }));
  };

  const handleSave = () => {
    if (!selectedTemplateId) return;
    onSave(selectedTemplateId, mapping);
  };

  const columns = [
    {
      title: 'Nxsys Onboarding Fields',
      dataIndex: 'field',
      width: '40%',
    },
    {
      title: 'Zoho Template Fields',
      dataIndex: 'zohoField',
      width: '60%',
      render: (_: any, record: { field: string }) => (
        <AutoComplete
          value={mapping[record.field] || ''}
          options={templateFields}
          onChange={(value) => handleMappingChange(record.field, value)}
          placeholder="Select or type field"
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
          className="w-full"
        />
      ),
    },
  ];

  const dataSource = ONBOARDING_FIELDS.map((field) => ({
    key: field,
    field,
  }));

  return (
    <div>
      <Row gutter={24} className="mb-4">
        <Col sm={24} md={12}>
          <div className="text-sm font-medium mb-1">Select Template</div>
          <AppSelect
            options={templateOptions}
            value={selectedTemplateId ? templateOptions.find((o: any) => o.value === selectedTemplateId) : undefined}
            onChange={handleSelectTemplate}
            placeholder="Select template"
            loading={templatesLoading}
            className="w-full"
          />
        </Col>
      </Row>
      {selectedTemplateId && (
        <>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            loading={detailsLoading}
            size="small"
          />
          <div className="mt-4">
            <AppButton
              type="primary"
              name="Save Mapping"
              onClick={handleSave}
              loading={saving}
            />
          </div>
        </>
      )}
    </div>
  );
};

const ZohoSettingsForm: React.FC<ZohoSettingsFormProps> = ({
  companyData,
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const [form] = Form.useForm();
  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);
  const { value: zohoSettings, run: fetchZohoSettings } = useGetZohoSettings();
  const { run: connectZoho, loading: isConnecting } = useConnectZoho();
  const { run: updateZohoSettings, loading: isUpdatingMapping } = useUpdateZohoSettings();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(ZohoTab.CONNECT);

  const hasSavedCredentials = !!(zohoSettings?.clientId && zohoSettings?.clientSecret);

  useEffect(() => {
    const existRefreshToken = !!zohoSettings?.refreshToken;
    const existAccessToken = !!zohoSettings?.accessToken;
    const existExpireAt = !!zohoSettings?.expireAt;
    const isNotExpire =
      existExpireAt &&
      new Date(zohoSettings?.expireAt ?? '')?.getTime() > new Date().getTime();
    setIsAuthorized(
      existRefreshToken || (existAccessToken && existExpireAt && isNotExpire),
    );
  }, [zohoSettings]);

  useEffect(() => {
    if (companyData?.id) {
      fetchZohoSettings(companyData.id);
    }
  }, [companyData?.id]);

  useEffect(() => {
    if (zohoSettings && needReinitializeForm) {
      form.setFieldsValue({
        clientId: zohoSettings.clientId || '',
        clientSecret: zohoSettings.clientSecret || '',
      });
      setNeedReinitializeForm(false);
    }
  }, [zohoSettings]);

  // Listen socket for Zoho OAuth callback (backend emits after token received).
  //
  // The event name is keyed by companyId + companyParentId. The legacy portal
  // subscribes to `zoho-sign-contract-<companyId>-<parentId>` — repeating the
  // namespace as a prefix — while this app was listening to the bare id pair, so
  // only one of the two can be matching what the backend emits. Subscribe to
  // both until it is confirmed which, then drop the other.
  useEffect(() => {
    if (!companyData?.id) return;
    const companyId = companyData.id;
    const parentCompanyId = companyData.companyParentId || companyId;
    const idPair = `${companyId}-${parentCompanyId}`;
    const channels = [idPair, `zoho-sign-contract-${idPair}`];

    const handleZohoVerified = () => {
      // Refetch instead of merging the socket payload — the settings endpoint is
      // the authoritative view of the connection state.
      fetchZohoSettings(companyId);
    };

    channels.forEach((channel) =>
      socketZohoSignContract.on(channel, handleZohoVerified),
    );
    return () => {
      channels.forEach((channel) =>
        socketZohoSignContract.off(channel, handleZohoVerified),
      );
    };
  }, [companyData?.id, companyData?.companyParentId]);

  const handleAuthorize = async () => {
    if (!companyData?.id) return;
    try {
      const res = await connectZoho(companyData.id);
      if (res?.url) {
        window.open(res.url, '_blank');
      }
    } catch {
      showMessage({ type: 'error', content: 'Failed to connect Zoho' });
    }
  };

  const handleTabChange = (key: string) => {
    if (key !== ZohoTab.CONNECT && !isAuthorized) {
      showMessage({
        type: 'warning',
        content: 'Zoho expired or not connected yet. Please connect Zoho before!',
      });
      return;
    }
    setActiveTab(key);
  };

  const handleMappingSave = async (
    mappingKey: string,
    templateId: string,
    mapping: Record<string, string>,
  ) => {
    if (!companyData?.id) return;
    await updateZohoSettings(companyData.id, {
      [`${mappingKey}TemplateId`]: templateId,
      [`${mappingKey}Mapping`]: mapping,
    } as any);
    showMessage({ type: 'success', content: messages['common.updateSuccess'] });
    fetchZohoSettings(companyData.id);
  };

  const onFinish = async (values: ZohoSettingsFormValues) => {
    if (!companyData?.id) return;
    await updateZohoSettings(companyData.id, {
      clientId: values.clientId || '',
      clientSecret: values.clientSecret || '',
    });
    showMessage({ type: 'success', content: messages['common.updateSuccess'] });
    setIsEditing(false);
    fetchZohoSettings(companyData.id);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  const onValuesChange = () => {
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  // NX-5228: Ireland's umbrella models are PAYE, RCT (stored as CIS) and
  // Umbrella — see UMBRELLA_IRELAND_PAYMENT_MODELS. Non-CIS, Umbrella Hybrid
  // and PAYE Hybrid are UK-only, so an Irish tenant was being offered Zoho
  // templates for models it can never have, under UK names.
  const templateTabs = isBureau
    ? TEMPLATE_TABS.filter((t) => t.key === ZohoTab.PAYE)
    : isIreland
      ? TEMPLATE_TABS.filter((t) =>
          [ZohoTab.CIS, ZohoTab.PAYE, ZohoTab.UMBRELLA].includes(t.key),
        ).map((t) =>
          t.key === ZohoTab.CIS ? { ...t, tab: 'RCT TEMPLATE' } : t,
        )
      : TEMPLATE_TABS;

  const tabs = [
    { tab: 'CONNECT ZOHO', key: ZohoTab.CONNECT },
    ...templateTabs.map((t) => ({
      ...t,
      disabled: !isAuthorized,
    })),
  ];

  const renderTabContent = () => {
    if (activeTab === ZohoTab.CONNECT) {
      return (
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          {isAuthorized && !isEditing && (
            <AppAlert
              type="success"
              variant="success"
              message={messages['common.zohoIsAlreadyConnected']}
              className="mb-[12px]"
            />
          )}
          <Row gutter={24}>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.clientId']}
                name="clientId"
                rules={[
                  { required: true, message: messages['form.pleaseEnter'] },
                ]}
              >
                <AppInput
                  placeholder={messages['form.enter']}
                  allowCapitalize={false}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.clientSecret']}
                name="clientSecret"
                rules={[
                  { required: true, message: messages['form.pleaseEnter'] },
                ]}
              >
                <AppInput
                  placeholder={messages['form.enter']}
                  allowCapitalize={false}
                />
              </Form.Item>
            </Col>
          </Row>
          <div className="flex items-center gap-3 mt-2">
            {isEditing && (
              <AppButton
                type="primary"
                name={messages['common.save']}
                htmlType="submit"
                loading={isUpdatingMapping}
                leftIcon={
                  <Icons.IconCheckCircle
                    color={defaultTheme.theme.palette.white}
                  />
                }
              />
            )}
            {hasSavedCredentials && !isEditing && !isAuthorized && (
              <AppButton
                type="primary"
                name="Connect Zoho"
                onClick={handleAuthorize}
                loading={isConnecting}
                leftIcon={
                  <Icons.IconLink color={defaultTheme.theme.palette.white} />
                }
              />
            )}
          </div>
        </AppFormContainer>
      );
    }

    const templateTab = TEMPLATE_TABS.find((t) => t.key === activeTab);
    if (templateTab && companyData?.id) {
      return (
        <TemplateMappingTab
          key={templateTab.mappingKey}
          companyId={companyData.id}
          mappingKey={templateTab.mappingKey}
          zohoSettings={zohoSettings}
          onSave={(templateId, mapping) =>
            handleMappingSave(templateTab.mappingKey, templateId, mapping)
          }
          saving={isUpdatingMapping}
        />
      );
    }

    return null;
  };

  return (
    <AppCardTabListContainer
      tabList={tabs}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      className="!border-none"
    >
      <div className="pt-4">{renderTabContent()}</div>
    </AppCardTabListContainer>
  );
};

export default ZohoSettingsForm;
